<template>
  <div class="searchExams" ref="searchExams">
    <div class="searchExams__list">
      <div class="searchExams__list--cart" @click="goToCart">
        <p class="searchExams__title">{{ examsListTitle }}</p>
        <div>
          <span class="searchExams__list--cart-amount" v-if="selectedExams.length">{{
            selectedExams.length
          }}</span>
          <CartFillSvg class="searchExams__list--cart-icon" v-if="selectedExams.length" />
          <CartSvg class="searchExams__list--cart-icon" v-else />
        </div>
      </div>

      <VInputWithIcon
        id="input-el"
        class="searchExams__input"
        placeholder="busque pelo nome do serviço"
        v-model="queryExams"
        @focus="handleSearchFocus"
        @closeIcon="clearSearchBar"
        :hasInput="hasInput"
      >
        <SearchSvg />
      </VInputWithIcon>

      <SearchSkeleton v-if="loader" />
      <div v-if="!loader">
        <div v-if="notFoundExams">
          <NotFoundExamsPartial />
        </div>

        <div v-else-if="!queryExams && highlightedExams.length">
          <p class="searchExams__list--header-bold">
            {{ highlightedExams.length }} exames mais adicionados
          </p>
          <div v-for="(exam, idx) in highlightedExams" :key="exam.id" class="searchExams__card">
            <ExamListCardPartial
              :exam="exam"
              :idx="idx"
              @addExamClick="addExam"
              @removeExamClick="openDrawerToRemove"
            />
          </div>
        </div>

        <div v-else-if="searchResult.length">
          <p class="searchExams__result--text">
            Exibindo resultado para
            <span class="searchExams__result--text-bold"> {{ queryExams }}</span>
          </p>
          <p class="searchExams__result--text">
            <span class="searchExams__result--text-bold">{{ searchResult.length }}</span> exame(s)
            encontrado(s)
          </p>
          <div class="searchExams__result--href" @click="clearSearchBar">Limpar busca</div>

          <div v-for="(exam, idx) in searchResult" :key="exam.id" class="searchExams__card">
            <ExamListCardPartial
              :exam="exam"
              :idx="idx"
              @addExamClick="addExam"
              @removeExamClick="openDrawerToRemove"
              :hideSynonymy="false"
            />
          </div>
        </div>
      </div>
    </div>
    <DrawerBottom
      :open="openDrawer"
      @toggle="openDrawer = !openDrawer"
      @close="closeDrawer"
      v-if="openDrawer"
    >
      <template #content>
        <div class="drawer-content" v-if="shouldRemoveExam">
          <h3>Deseja excluir exame: {{ selectedExamToRemove.name }}</h3>
          <div class="drawer-buttons">
            <button @click="removeExam({}, selectedExamToRemove)">Excluir</button>
            <button @click="closeDrawer">Voltar</button>
          </div>
        </div>
      </template>
    </DrawerBottom>
    <div class="searchExams__footer">
      <VButton
        text="Não encontrei meu exame"
        variant="secondary"
        @click.native="goToConclusion"
        v-if="highlightedExams.length && !selectedExams.length"
      />
      <VButton
        text="Continuar sem exames"
        variant="secondary"
        @click.native="goToConclusion"
        v-if="!highlightedExams.length && !selectedExams.length"
      />
      <VButton
        v-else-if="selectedExams.length"
        class="searchExams__footer--button"
        text="Ver carrinho"
        variant="primary"
        @click.native="goToCart"
      />
    </div>
  </div>
</template>

<script>
import VInputWithIcon from '@/components/commons/VInputWithIcon.vue';
import { debounce } from 'lodash';
import SearchSkeleton from './partials/searchSkeleton.partial.vue';
import NotFoundExamsPartial from './partials/notFoundExams.partial.vue';
import ExamListCardPartial from './partials/examListCard.partial.vue';
import DrawerBottom from '@/components/drawerBottom.component.vue';
import { dataLayersMixin } from '@/utils/mixins';
import { mapState, mapActions, mapMutations } from 'vuex';

export default {
  name: 'search-exams',
  components: {
    VInputWithIcon,
    SearchSkeleton,
    NotFoundExamsPartial,
    ExamListCardPartial,
    DrawerBottom,
  },
  mixins: [dataLayersMixin],
  data() {
    return {
      loader: false,
      showSelectedExams: false,
      examsListTitle: 'Adicione seus exames',
      queryExams: '',
      searchResult: [],
      selectedExams: [],
      debounce: false,
      showNotFoundExams: false,
      openDrawer: false,
      highlightedExams: [],
      shouldRemoveExam: false,
      selectedExamToRemove: [],
    };
  },
  watch: {
    // whenever input changes, this function will run
    queryExams() {
      this.debouceSearchExams();
    },
  },
  created() {
    this.mostCommonPagesAnalytics({
      ...(this.highlightedExamsState?.length
        ? { step: '5_exames_mais_selecionados' }
        : { step: 'seleção_de_serviço' }),
      screen: 'busca',
      complementaryObject: {
        payment: {
          type: 'particular',
        },

        previous_choice: 'Selecionar exames ou vacinas',
      },
    });
    // _.debounce is a function provided by lodash to limit how
    // often a particularly expensive operation can be run.
    // In this case, we want to limit how often we access
    // yesno.wtf/api, waiting until the user has completely
    // finished typing before making the ajax request. To learn
    // more about the _.debounce function (and its cousin
    // _.throttle), visit: https://lodash.com/docs#debounce
    this.debouceSearchExams = debounce(this.handleSearch, 500);
    if (this.currentAdmission.medicalOrders) {
      const { exams = [] } = this.currentAdmission.medicalOrders[0];
      if (exams.length) {
        return this.goNextStep('questions_step');
      }
    }
    if (this.selectedExamsState.length) {
      this.selectedExams = this.selectedExamsState;
    }
    this.highlightedExams =
      this.highlightedExamsState &&
      this.highlightedExamsState.map((highlightedExam) => {
        const examIsSelected = this.selectedExamsState.find(
          (exam) => exam.id === highlightedExam.id,
        );
        return { ...highlightedExam, added: examIsSelected };
      });
  },
  mounted() {
    document.body.style.overflow = null;
  },
  computed: {
    ...mapState({
      biologicalSex: ({ mdm }) => mdm.data.biologicalSex,
      selectedExamsState: ({ searchExams }) => searchExams.data.manuallySelectedExams,
      selectedUnit: ({ brandUnit }) => brandUnit.data.selectedUnit.idUnidade,
      selectedBrandId: ({ brandUnit }) => brandUnit.data.currentBrand.brandId,
      highlightedExamsState: ({ brandUnit }) => brandUnit.data.unitConfigs.checkin.highlightedExams,
      currentAdmission: ({ currentAdmission }) => currentAdmission.data,
    }),
    hasInput() {
      return !!this.queryExams.length;
    },
    notFoundExams() {
      return this.showNotFoundExams;
    },
    userBiologicalSex() {
      if (!this.biologicalSex) {
        return 'A';
      }
      return this.biologicalSex[0];
    },
  },
  methods: {
    ...mapActions(['searchExamsAPI', 'global/updateFlowSteps', 'setAdmissionNotElegible']),
    ...mapMutations({
      setSelectedExams: 'SET_SELECTED_EXAMS',
      clearQuestionnarie: 'SET_RESET_QUESTIONNAIRE',
    }),
    openDrawerToRemove(_, exam) {
      this.$refs.searchExams.scrollIntoView({ block: 'start', scrollBehavior: 'smooth' });
      document.body.style.overflow = 'hidden';
      this.shouldRemoveExam = true;
      this.openDrawer = true;
      this.selectedExamToRemove = exam;
    },
    clearExamsSelected() {
      this.selectedExams = [];
      this.highlightedExams = this.highlightedExams.map((exam) => ({ ...exam, added: false }));
      this.searchResult = this.searchResult.map((exam) => ({ ...exam, added: false }));
      this.setSelectedExams([]);
    },
    goToCart() {
      this.removeToast();
      return this.goNextStep('cart');
    },
    async goToConclusion(text) {
      await this.setAdmissionNotElegible();
      document.body.style.overflow = null;

      return this.$router.push({ name: 'exams-skip', params: { previous_choice: text } });
    },
    showDrawer() {
      this.openDrawer = true;
      this.$refs.searchExams.scrollIntoView({ block: 'start', scrollBehavior: 'smooth' });
      document.body.style.overflow = 'hidden';
    },
    closeDrawer() {
      this.openDrawer = false;
      this.shouldRemoveExam = false;
      document.body.style.overflow = null;
    },

    verifyExamsResponse() {
      this.showNotFoundExams = !this.searchResult.length && !!this.queryExams.length;
    },
    handleCloseSearch() {
      this.examsListTitle = 'Adicione seus exames';
      this.queryExams = '';
    },
    clearSearchBar() {
      this.queryExams = '';
    },
    goToSearchBar() {
      this.queryExams = '';
      const toFocusField = document.getElementById('input-el');
      if (toFocusField) {
        const toFocusFieldChild = toFocusField.firstChild.childNodes[1];
        toFocusFieldChild.focus();
      }
      this.verifyExamsResponse();
    },
    handleSearchFocus() {
      this.examsListTitle = 'Adicione seus exames';
      this.verifyExamsResponse();
    },
    setHighLightedExams(exams) {
      this.highlightedExams = [...exams];
    },
    removeToast() {
      const toast = document.querySelectorAll('.toasted');
      if (toast.length) {
        toast.forEach((eachToast) => (eachToast.style.display = 'none'));
      }
    },
    addExam(_, exam) {
      this.removeToast();
      const hasSelectedSameExam = this.selectedExams.find(
        (highlightedExam) => highlightedExam.id === exam.id,
      );
      const hasExamHighlighted = this.highlightedExams.find(
        (highlightedExam) => highlightedExam.id === exam.id,
      );
      const includedSearchExam = this.searchResult.find(
        (searchExamResult) => searchExamResult.id === exam.id,
      );

      if (hasSelectedSameExam) {
        return;
      }

      if (includedSearchExam) {
        const searchResultUpdated = this.searchResult.map((searchResultExam) => {
          if (searchResultExam.id === exam.id) {
            searchResultExam.added = true;
          }
          return searchResultExam;
        });
        this.searchResult = [...searchResultUpdated];
      }

      if (hasExamHighlighted) {
        const highlightedExamsUpdated = this.highlightedExams.map((highlightedExam) => {
          if (highlightedExam.id === exam.id) {
            highlightedExam.added = true;
          }
          return highlightedExam;
        });
        this.highlightedExams = [...highlightedExamsUpdated];
      }
      this.selectedExams = [...this.selectedExams, { ...exam, added: true }];

      this.setSelectedExams(this.selectedExams);
      this.examsListTitle = 'Adicione seus exames';
      this.verifyExamsResponse();
      this.clearQuestionnarie();
      this.$toasted.show('1 item adicionado', {
        position: 'bottom-center',
        duration: 5000,
        fullWidth: false,
        className: 'toast-action',
        action: [
          {
            text: 'Ver carrinho',
            onClick: () => {
              this.removeToast();
              this.goToCart();
            },
          },
        ],
      });

      this.mostCommonPagesAnalytics({
        eventType: 'service_selection_add_to_cart',
        step: 'seleção_de_serviços',
        screen: 'busca',
        items: this.selectedExams.map(({ id, name, price }) => ({
          id,
          name,
          price,
          quantity: 1,
        })),
        complementaryObject: {
          payment: {
            type: 'particular',
          },
        },
      });
    },
    removeExam(_, exam) {
      this.removeToast();
      this.closeDrawer();

      const oldSelectedExams = [...this.selectedExams];
      const oldHighlightedExams = JSON.stringify(this.highlightedExams);

      const newSelectedExams = this.selectedExams.filter(
        (selectedExam) => selectedExam.id !== exam.id,
      );

      const hasExamHighlighted = this.highlightedExams.find(
        (highlightedExam) => highlightedExam.id === exam.id,
      );

      if (hasExamHighlighted) {
        this.highlightedExams.forEach((highlightedExam) => {
          if (highlightedExam.id === exam.id) {
            highlightedExam.added = false;
          }
        });
      }

      if (this.searchResult.length) {
        this.searchResult.forEach((searchResult) => {
          if (searchResult.id === exam.id) {
            searchResult.added = false;
          }
        });
      }

      this.selectedExams = newSelectedExams;
      this.setSelectedExams(newSelectedExams);
      this.clearQuestionnarie();
      this.$toasted.show('1 item excluído', {
        position: 'bottom-center',
        duration: 5000,
        fullWidth: false,
        className: 'toast-action',
        singleton: true,
        action: [
          {
            text: 'Desfazer',
            onClick: (e, toastObject) => {
              this.selectedExams = oldSelectedExams;
              this.setSelectedExams(oldSelectedExams);
              this.highlightedExams = JSON.parse(oldHighlightedExams);
              toastObject.goAway(0);
            },
          },
        ],
      });

      this.mostCommonPagesAnalytics({
        eventType: 'service_selection_remove_from_cart',
        step: 'seleção_de_serviços',
        screen: 'busca',

        items: this.selectedExams.map(({ id, name, price }) => ({
          id,
          name,
          price,
          quantity: 1,
        })),

        complementaryObject: {
          payment: {
            type: 'particular',
          },
        },
      });
    },
    async handleSearch() {
      if (this.queryExams.length === 0) {
        this.searchResult = [];
      }
      if (this.queryExams.length > 2) {
        this.loader = true;
        this.debounce = true;
        try {
          const result = await this.searchExamsAPI({
            searchQuery: this.queryExams,
            unitId: this.selectedUnit,
            brandId: this.selectedBrandId,
            sizeSearch: 50,
            page: 0,
            requiresSchedule: false,
            orderByVolumetry: true,
            specificGender: this.userBiologicalSex,
            includePrice: true,
            searchByHighlight: true,
          });

          this.searchResult = result
            .filter((exam) => exam.price && !this.selectedExams.find(({ id }) => exam.id === id))
            .map((item, idx) => ({ ...item, position: idx + 1, added: false }));
        } catch (err) {
          this.searchResult = [];
        } finally {
          this.verifyExamsResponse();
          this.debounce = false;
          this.loader = false;
        }
      }
    },
  },
};
</script>

<style lang="scss">
.searchExams {
  width: 100%;
  scrollbar-width: none;

  &__input {
    padding-bottom: 14px;
  }

  &__result {
    &--text {
      font-size: 14px;
      line-height: 170%;
      text-align: left;
    }

    &--text-bold {
      font-size: 14px;
      line-height: 170%;
      text-align: left;
      font-weight: var(--font-weight-bold);
    }

    &--href {
      color: var(--color-interaction-accent-1);
      font-weight: var(--font-weight-bold);
      font-size: 14px;
      text-align: right;
      margin-top: -14px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 4px;

      * {
        fill: var(--color-interaction-accent-1);
      }
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
  }

  &__list {
    overflow: hidden;
    padding-bottom: 185px;
    padding-top: 80px;

    &--header {
      text-align: left;
      font-size: var(--font-size-2);
      line-height: 170%;
      color: #363636;
      padding-top: 16px;
    }

    &--header-bold {
      text-align: left;
      font-size: var(--font-size-2);
      line-height: 170%;
      color: #363636;
      padding-top: 16px;
      font-weight: var(--font-weight-bold);
    }

    &--cart {
      display: flex;
      position: relative;
      justify-content: space-between;
      align-items: center;
    }

    &--cart-amount {
      position: absolute;
      right: 0;
      font-size: var(--font-size-1);
      background-color: white;
      border-radius: 20px;
      border: 1.5px solid var(--color-interaction-accent-1);
      padding: 1px 4px;
      color: var(--color-interaction-accent-1);
      z-index: 1;
    }

    &--cart-icon {
      right: 24px;
    }
  }

  &__footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    max-width: inherit;
    margin: 0 auto;
    padding: 16px 12px;
    border-top: 1px solid #e5e5e5;
    background-color: #ffffff;

    &--button {
      margin-bottom: 8px;
    }
  }

  &__title {
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-3);
    line-height: 150%;
    text-align: left;
    padding-bottom: 8px;
  }

  &__subtitle {
    line-height: 170%;
    font-size: var(--font-size-2);
    text-align: left;
    padding-bottom: 24px;
  }

  .center {
    margin: 44px auto;
  }

  .totalAmount {
    display: flex;
    justify-content: space-between;
    padding-bottom: 16px;

    &__label {
      color: #363636;
      font-size: var(--font-size-3);
      line-height: 24px;
      font-weight: var(--font-weight-bold);
    }

    &__value {
      color: var(--color-grey-8);
      font-size: var(--font-size-3);
      font-weight: var(--font-weight-medium);
    }
  }

  .drawer-content {
    h3 {
      font-weight: 700;
      font-size: 16px;
      line-height: 24px;
      text-align: center;
      color: #161616;
      margin-bottom: 24px;
    }

    p {
      font-weight: 400;
      font-size: 16px;
      line-height: 18.75px;
      text-align: center;
      color: var(--color-grey-11);
      margin-bottom: 24px;
    }

    .drawer-buttons {
      display: flex;
      flex-direction: column;

      button {
        border-radius: 8px;
        background-color: var(--color-primary);
        border: 1px solid var(--color-primary);
        color: white;
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        padding: 12px 0;
        width: 100%;

        &:nth-child(2) {
          margin-top: 16px;
          border-color: transparent;
          background-color: transparent;
          color: var(--color-primary);
        }
      }
    }
  }
}
</style>
